import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

// ✅ Define reusable button styles (variants) using class-variance-authority (CVA)
const buttonVariants = cva(
  // Base styles applied to every button
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      // 🎨 Define variants for different button styles
      variant: {
        primary:
          // ✅ Your selected primary variant: Imperial Red background, subtle hover animation
          "bg-imperial-red text-white shadow rounded-lg hover:bg-imperial-red/90 hover:scale-105 hover:shadow-lg",

        secondary:
          // ✅ Secondary variant: Mikado Yellow, softer yet distinct
          "bg-mikado-yellow text-stone-brown shadow rounded-lg hover:bg-mikado-yellow/90 hover:scale-105 hover:shadow-md",

          negative:
          // ✅ Negative variant: softer yet distinct - conveys undesired behaviour
          "bg-stone-brown text-swan shadow rounded-lg hover:shadow-md",

        outline:
          // ✅ Outline variant, minimalistic, professional for less important actions
          "border border-pebble-brown text-pebble-brown shadow-sm rounded-lg hover:bg-pebble-brown hover:text-white hover:scale-105",

        ghost:
          // ✅ Ghost variant, subtle with transparent bg and soft hover
          "text-imperial-red rounded-lg hover:bg-imperial-red/90",

        link:
          // ✅ Link variant, ideal for inline text buttons
          "text-imperial-red underline-offset-4 hover:underline",
      },
      size: {
        // 🎚️ Size variations to maintain consistency
        default: "h-9 px-4 py-2",
        sm: "h-8 px-3 text-xs",
        lg: "h-10 px-8",
        icon: "h-9 w-9",
      },
    },

    // 🚨 Default variant: ensures buttons have a default style if no props are passed
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
);

// React forwardRef allows passing refs to button for external DOM interactions
const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    // "asChild" from Radix UI enables this button to render as a different element (like <a>)
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        // Classname combines the base variant, any variant/size modifiers, plus additional passed classes
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);

/**
 * CloseIconButton
 * 🔹 Compact close button used for thumbnails or modals.
 * 🔹 Reuses Button with variant="ghost" and size="icon"
 */
export const CloseIconButton = ({ onClick, className = "" }) => (
  <button
    onClick={onClick}
    className={cn(
      "bg-pebble-brown text-imperial-red rounded-lg w-5 h-5 flex items-center justify-center",
      className
    )}
  >
    <X size={18}/>
  </button>
);

// Display name for debugging and readability
Button.displayName = "Button";

// ✅ Export the Button component and its variant utility for reuse across the app
export { Button, buttonVariants };